import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import QRCode from 'qrcode'
import { Center } from '@astryxdesign/core/Center'
import { Stack } from '@astryxdesign/core/Stack'
import { Card } from '@astryxdesign/core/Card'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { Banner } from '@astryxdesign/core/Banner'
import { Spinner } from '@astryxdesign/core/Spinner'
import { StatusDot } from '@astryxdesign/core/StatusDot'
import { startLogin, claimLogin, watchLogin } from './consoleApi'
import { stateLine, countdownText } from './consoleText'

// The console's login page: a QR code for a club admin to scan from the app,
// and nothing to type. Built from the login-card template - centred card on
// a plain ground - with the form replaced by the code.
//
// The code lasts two minutes and the page asks for a new one when it runs
// out, but only a few times: a tab left open overnight should not keep
// writing login requests. After that it waits for a click.
const MAX_AUTOMATIC_CODES = 5

// Standalone page, as in the template: it paints its own ground.
const pageStyle = {
    minHeight: '100vh',
    backgroundColor: 'var(--color-background-body)',
}
const contentStyle = {
    width: '100%',
    maxWidth: 400,
}
// The QR code is an image with its own quiet zone. Black on white whatever
// the colour scheme: a phone camera reads that reliably, and an inverted code
// is the classic reason a scan fails on a dark page.
const qrStyle = {
    width: 256,
    height: 256,
    borderRadius: 'var(--radius-md)',
}

export default function ConsoleLogin({ notice, onLoggedIn }) {
    const [phase, setPhase] = useState('starting') // starting | showing | paused | unavailable
    const [login, setLogin] = useState(null) // { id, expiresAt, image }
    const [state, setState] = useState('pending')
    const [now, setNow] = useState(() => Date.now())
    const stopWatching = useRef(() => {})
    const automaticCodes = useRef(0)
    // The countdown reaching zero and the server saying "expired" arrive
    // within a second of each other; one new code is enough.
    const starting = useRef(false)

    const begin = useCallback(async (automatic) => {
        if (starting.current) return
        starting.current = true
        try {
            await startCode(automatic)
        } finally {
            starting.current = false
        }
    // startCode is defined below and closes over the same refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onLoggedIn])

    const startCode = async (automatic) => {
        stopWatching.current()
        if (automatic) {
            automaticCodes.current += 1
            if (automaticCodes.current > MAX_AUTOMATIC_CODES) {
                setPhase('paused')
                return
            }
        } else {
            automaticCodes.current = 1
        }

        setPhase('starting')
        setState('pending')

        let started
        try {
            started = await startLogin()
        } catch (e) {
            setPhase('unavailable')
            return
        }
        if (started.status !== 200 || !started.json) {
            setPhase('unavailable')
            return
        }

        const { id, payload, expires_at: expiresAt } = started.json
        const image = await QRCode.toDataURL(payload, { width: 256, margin: 2, errorCorrectionLevel: 'M' })

        setLogin({ id, expiresAt: Number(expiresAt), image })
        setPhase('showing')

        stopWatching.current = watchLogin(id, async next => {
            setState(next)
            if (next === 'approved') {
                stopWatching.current()
                const claimed = await claimLogin(id).catch(() => ({ status: 0 }))
                if (claimed.status === 200) {
                    onLoggedIn()
                } else {
                    begin(true)
                }
            } else if (next === 'expired') {
                begin(true)
            } else if (next === 'refused' || next === 'claimed') {
                stopWatching.current()
            }
        }, () => begin(true))
    }

    useEffect(() => {
        begin(false)
        return () => stopWatching.current()
    }, [begin])

    // The countdown, and a new code when it reaches zero before anybody has
    // scanned it - the server says "expired" too, this is in case the stream
    // is slow to.
    useEffect(() => {
        if (phase !== 'showing') return undefined
        const timer = setInterval(() => {
            const at = Date.now()
            setNow(at)
            if (state === 'pending' && login && at / 1000 >= login.expiresAt) {
                begin(true)
            }
        }, 1000)
        return () => clearInterval(timer)
    }, [phase, state, login, begin])

    return (
        <Center axis="both" padding={6} style={pageStyle}>
            <Stack gap={4} hAlign="center" style={contentStyle}>
                <Stack gap={2} hAlign="center">
                    <img src="/img/logo-padelr-2048.png" alt="" width={48} height={48} />
                    <Text weight="bold" size="lg">Padelr</Text>
                </Stack>

                <Card padding={8} width="100%">
                    <Stack gap={4} hAlign="stretch">
                        <Stack gap={1} hAlign="center">
                            <Heading level={2}>Console club</Heading>
                            <Text color="secondary" size="sm">Connexion depuis l'app Padelr</Text>
                        </Stack>

                        {notice && <Banner status="warning" title={notice} />}

                        <LoginBody
                            phase={phase}
                            login={login}
                            state={state}
                            now={now}
                            onRestart={() => begin(false)}
                        />
                    </Stack>
                </Card>

                <Text type="supporting" color="secondary" justify="center">
                    Réservé aux administrateurs de club. La session dure 8 heures.
                </Text>
            </Stack>
        </Center>
    )
}

ConsoleLogin.propTypes = {
    notice: PropTypes.string,
    onLoggedIn: PropTypes.func.isRequired,
}

function LoginBody({ phase, login, state, now, onRestart }) {
    if (phase === 'starting') {
        return (
            <Center axis="both" padding={8}>
                <Spinner label="Préparation du code" />
            </Center>
        )
    }

    if (phase === 'unavailable') {
        return (
            <Stack gap={4} hAlign="stretch">
                <Banner status="error" title="La connexion à la console est indisponible pour le moment." />
                <Button label="Réessayer" variant="secondary" onClick={onRestart} />
            </Stack>
        )
    }

    if (phase === 'paused' || state === 'refused') {
        return (
            <Stack gap={4} hAlign="stretch">
                <Text justify="center">
                    {state === 'refused' ? stateLine('refused') : 'Le code a expiré.'}
                </Text>
                <Button label="Afficher un nouveau code" variant="primary" onClick={onRestart} />
            </Stack>
        )
    }

    const scanned = state === 'scanned' || state === 'approved'

    return (
        <Stack gap={4} hAlign="center">
            <img src={login.image} alt="Code QR de connexion à la console" style={qrStyle} />
            <Stack direction="horizontal" gap={2} vAlign="center" hAlign="center">
                {scanned && <StatusDot variant="accent" label="Code scanné" isPulsing />}
                <Text justify="center" size="sm">{stateLine(state)}</Text>
            </Stack>
            {!scanned && (
                <Text type="supporting" color="secondary" hasTabularNumbers>
                    {countdownText(login.expiresAt, now)}
                </Text>
            )}
        </Stack>
    )
}

LoginBody.propTypes = {
    phase: PropTypes.string.isRequired,
    login: PropTypes.object,
    state: PropTypes.string.isRequired,
    now: PropTypes.number.isRequired,
    onRestart: PropTypes.func.isRequired,
}
